import React, { useEffect, useState } from 'react';
import { motion, useAnimation, useInView, AnimatePresence } from 'framer-motion';
import { Sparkles, Gift, DollarSign, ArrowDown, Star } from 'lucide-react';

const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);
  const controls = useAnimation();
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  useEffect(() => {
    if (isInView) {
      setIsVisible(true);
      controls.start('visible');
    }
  }, [isInView, controls]);

  const scrollToOffers = () => {
    const el = document.getElementById('featured-offers');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const floatingElements = [
    { icon: <DollarSign size={24} />, delay: 0, color: 'from-green-400 to-emerald-600' },
    { icon: <Gift size={24} />, delay: 0.3, color: 'from-red-400 to-rose-600' },
    { icon: <Star size={24} />, delay: 0.6, color: 'from-yellow-400 to-amber-600' },
    { icon: <Sparkles size={24} />, delay: 0.9, color: 'from-purple-400 to-violet-600' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
        duration: 0.8,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 12,
      },
    },
  };

  const floatAnimation = {
    initial: { y: 0 },
    animate: (delay) => ({
      y: [0, -20, 0],
      transition: {
        delay,
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut',
      },
    }),
  };

  const gradientAnimation = {
    initial: { backgroundPosition: '0% 50%' },
    animate: {
      backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
      transition: {
        duration: 5,
        repeat: Infinity,
        ease: 'linear',
      },
    },
  };

  return (
    <section ref={ref} className="relative w-full min-h-[85vh] md:min-h-[90vh] overflow-hidden bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Animated gradient background */}
      <motion.div
        className="absolute inset-0 opacity-30"
        animate={{
          background: [
            'radial-gradient(circle at 20% 50%, rgba(120, 119, 198, 0.3) 0%, transparent 50%)',
            'radial-gradient(circle at 80% 50%, rgba(255, 119, 198, 0.3) 0%, transparent 50%)',
            'radial-gradient(circle at 50% 20%, rgba(120, 119, 255, 0.3) 0%, transparent 50%)',
            'radial-gradient(circle at 20% 50%, rgba(120, 119, 198, 0.3) 0%, transparent 50%)',
          ],
        }}
        transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}
      />

      {/* Particle background */}
      <div className="absolute inset-0">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full"
            initial={{
              x: Math.random() * 100 + 'vw',
              y: Math.random() * 100 + 'vh',
              opacity: Math.random() * 0.5,
            }}
            animate={{
              y: [null, '-100vh'],
              opacity: [0.2, 0.8, 0.2],
            }}
            transition={{
              duration: Math.random() * 3 + 2,
              repeat: Infinity,
              delay: Math.random() * 2,
              ease: 'linear',
            }}
          />
        ))}
      </div>

      {/* Main content */}
      <div className="relative z-10 container mx-auto px-4 md:px-8 h-full flex items-center">
        <motion.div
          className="w-full max-w-4xl mx-auto"
          variants={containerVariants}
          initial="hidden"
          animate={controls}
        >
          {/* Badge */}
          <motion.div
            variants={itemVariants}
            className="inline-flex items-center gap-2 px-4 py-2 mt-2 rounded-full bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 mb-8 backdrop-blur-sm"
          >
            <Sparkles className="w-4 h-4 text-yellow-400" />
            <span className="text-sm font-medium bg-gradient-to-r from-purple-200 to-pink-200 bg-clip-text text-transparent">
              🎉 New Offers Added Daily
            </span>
          </motion.div>

          {/* Main heading */}
          <motion.h1
            variants={itemVariants}
            className="text-2xl md:text-7xl lg:text-8xl font-bold leading-tight mb-6"
          >
            <span className="block bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent">
              Your Daily Hub
            </span>
            <span className="block">
              <motion.span
                animate={gradientAnimation.animate}
                className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent bg-[length:200%_auto]"
              >
                For Free Rewards,
              </motion.span>
            </span>
            <span className="block">
              <motion.span
                animate={gradientAnimation.animate}
                className="bg-gradient-to-r from-green-400 via-yellow-400 to-red-400 bg-clip-text text-transparent bg-[length:200%_auto]"
              >
                Apps & Bonuses!
              </motion.span>
            </span>
          </motion.h1>

          {/* Subheading */}
          <motion.p
            variants={itemVariants}
            className="text-lg md:text-2xl text-gray-300 mb-10 max-w-2xl leading-relaxed"
          >
            Discover <span className="font-bold text-white">verified offers</span> across gaming, 
            shopping, and software. Updated{' '}
            <span className="text-yellow-400 font-bold">24/7</span> for you.
          </motion.p>

          {/* CTA Button */}
          <motion.div
            variants={itemVariants}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <motion.button
              onClick={scrollToOffers}
              className="group relative overflow-hidden bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold py-4 px-8 rounded-2xl text-sm shadow-2xl shadow-purple-500/30"
              whileHover="hover"
              initial="initial"
              animate="initial"
            >
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500"
                variants={{
                  initial: { x: '-100%' },
                  hover: { x: '100%' },
                }}
                transition={{ duration: 0.5 }}
              />
              <div className="relative flex items-center justify-center gap-3">
                <span>Explore Today's Top Picks</span>
                <motion.span
                  animate={{ y: [0, 5, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <ArrowDown className="w-5 h-5" />
                </motion.span>
              </div>
            </motion.button>
          </motion.div>

          {/* Stats */}
          <motion.div
            variants={itemVariants}
            className="flex flex-wrap gap-8 mt-16"
          >
            {[
              { value: '500+', label: 'Active Offers', color: 'text-green-400' },
              { value: '24/7', label: 'Updates', color: 'text-blue-400' },
              { value: '100%', label: 'Verified', color: 'text-yellow-400' },
              { value: 'Free', label: 'Forever', color: 'text-pink-400' },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
              >
                <div className={`text-lg md:text-2xl font-bold ${stat.color}`}>
                  {stat.value}
                </div>
                <div className="text-gray-400 text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Floating elements */}
      <AnimatePresence>
        {isVisible && floatingElements.map((element, index) => (
          <motion.div
            key={index}
            custom={element.delay}
            variants={floatAnimation}
            initial="initial"
            animate="animate"
            className={`absolute hidden lg:block p-4 rounded-2xl bg-gradient-to-br ${element.color} shadow-2xl backdrop-blur-sm border border-white/10`}
            style={{
              top: `${20 + index * 15}%`,
              right: `${5 + index * 5}%`,
            }}
          >
            <div className="text-white">
              {element.icon}
            </div>
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 1.5, repeat: Infinity }}
      >
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white/50 rounded-full mt-2" />
        </div>
      </motion.div>

      {/* Glow effects */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
    </section>
  );
};

export default Hero;